// Configuration pour filtrer les produits et catégories français

export interface FrenchFilterConfig {
  enabled: boolean;
  filterType: 'lang' | 'category' | 'tag' | 'attribute' | 'meta' | 'custom';
  language?: string;
  categorySlug?: string;
  categoryId?: number;
  tagSlug?: string;
  tagId?: number;
  attributeName?: string;
  attributeValue?: string;
  metaKey?: string;
  metaValue?: string;
  customFilter?: (item: any) => boolean;
}

// Configuration par défaut - à ajuster selon votre WooCommerce
export const frenchFilterConfig: FrenchFilterConfig = {
  enabled: true,
  filterType: process.env.REACT_APP_FRENCH_FILTER_TYPE as any || 'lang',
  
  // Si vous filtrez par langue (WPML/Polylang)
  language: process.env.REACT_APP_FRENCH_LANGUAGE || 'fr',
  
  // Si vous filtrez par catégorie, définissez le slug ou l'ID
  categorySlug: process.env.REACT_APP_FRENCH_CATEGORY_SLUG || 'francais',
  categoryId: process.env.REACT_APP_FRENCH_CATEGORY_ID ? 
    parseInt(process.env.REACT_APP_FRENCH_CATEGORY_ID) : undefined,
  
  // Si vous filtrez par tag
  tagSlug: process.env.REACT_APP_FRENCH_TAG_SLUG || 'francais',
  tagId: process.env.REACT_APP_FRENCH_TAG_ID ? 
    parseInt(process.env.REACT_APP_FRENCH_TAG_ID) : undefined,
  
  // Si vous filtrez par attribut (ex: langue=français)
  attributeName: process.env.REACT_APP_FRENCH_ATTRIBUTE_NAME || 'langue',
  attributeValue: process.env.REACT_APP_FRENCH_ATTRIBUTE_VALUE || 'francais',
  
  // Si vous filtrez par meta data
  metaKey: process.env.REACT_APP_FRENCH_META_KEY || 'language',
  metaValue: process.env.REACT_APP_FRENCH_META_VALUE || 'fr',
  
  // Filtre personnalisé (peut être modifié selon vos besoins)
  customFilter: (item: any) => {
    // Exemple : vérifier si le nom contient des caractères français
    const frenchPattern = /[àâäéèêëïîôùûüÿç]/i;
    return frenchPattern.test(item.name) || frenchPattern.test(item.description || '');
  }
};

// Fonction pour vérifier si un produit est français
export const isProductFrench = (product: any): boolean => {
  if (!frenchFilterConfig.enabled) return true;
  
  switch (frenchFilterConfig.filterType) {
    case 'lang':
      return product.lang === frenchFilterConfig.language;
      
    case 'category':
      return product.categories?.some((cat: any) => 
        (frenchFilterConfig.categorySlug && cat.slug === frenchFilterConfig.categorySlug) ||
        (frenchFilterConfig.categoryId && cat.id === frenchFilterConfig.categoryId)
      );
      
    case 'tag':
      return product.tags?.some((tag: any) => 
        (frenchFilterConfig.tagSlug && tag.slug === frenchFilterConfig.tagSlug) ||
        (frenchFilterConfig.tagId && tag.id === frenchFilterConfig.tagId)
      );
      
    case 'attribute':
      return product.attributes?.some((attr: any) => 
        attr.name === frenchFilterConfig.attributeName &&
        attr.options?.includes(frenchFilterConfig.attributeValue)
      );
      
    case 'meta':
      return product.meta_data?.some((meta: any) => 
        meta.key === frenchFilterConfig.metaKey &&
        meta.value === frenchFilterConfig.metaValue
      );
      
    case 'custom':
      return frenchFilterConfig.customFilter ? 
        frenchFilterConfig.customFilter(product) : true;
      
    default:
      return true;
  }
};

// Fonction pour vérifier si une catégorie est française
export const isCategoryFrench = (category: any): boolean => {
  if (!frenchFilterConfig.enabled) return true;
  
  switch (frenchFilterConfig.filterType) {
    case 'lang':
      return category.lang === frenchFilterConfig.language;
      
    case 'category':
      // Pour les catégories, on vérifie si c'est la catégorie française ou une sous-catégorie
      if (frenchFilterConfig.categorySlug && category.slug === frenchFilterConfig.categorySlug) {
        return true;
      }
      if (frenchFilterConfig.categoryId && category.id === frenchFilterConfig.categoryId) {
        return true;
      }
      // Vérifier si c'est une sous-catégorie de la catégorie française
      // (nécessiterait une logique plus complexe avec l'arbre des catégories)
      return false;
      
    case 'custom':
      return frenchFilterConfig.customFilter ? 
        frenchFilterConfig.customFilter(category) : true;
      
    default:
      // Pour les autres types de filtres, on affiche toutes les catégories
      // car le filtrage se fait au niveau des produits
      return true;
  }
};

// Fonction pour obtenir les paramètres de filtre pour l'API
export const getFrenchFilterParams = (): any => {
  // Toujours inclure le statut 'publish' pour exclure les brouillons
  const baseParams = { status: 'publish' };
  
  if (!frenchFilterConfig.enabled) return baseParams;
  
  switch (frenchFilterConfig.filterType) {
    case 'lang':
      // Pour le filtrage par langue, on pourrait avoir un paramètre API
      // mais généralement ce filtrage se fait côté client
      // Si votre API supporte un paramètre lang, décommentez la ligne suivante :
      // return { ...baseParams, lang: frenchFilterConfig.language };
      return baseParams;
      
    case 'category':
      return frenchFilterConfig.categoryId ? 
        { ...baseParams, category: frenchFilterConfig.categoryId.toString() } : baseParams;
        
    case 'tag':
      return frenchFilterConfig.tagId ? 
        { ...baseParams, tag: frenchFilterConfig.tagId.toString() } : baseParams;
        
    case 'attribute':
      return {
        ...baseParams,
        attribute: frenchFilterConfig.attributeName,
        attribute_term: frenchFilterConfig.attributeValue
      };
      
    default:
      return baseParams;
  }
};
